import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertCircle, RefreshCw } from 'lucide-react'
import { getScreenerTickers } from '@/lib/api'
import { cn } from '@/lib/utils'
import { useAuth } from '@/hooks/useAuth'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { useOrderbooksScreenerData } from '@/hooks/useOrderbooksScreenerData'
import { useScreenerOrderbookPresets } from '@/hooks/useScreenerOrderbookPresets'
import { useScreenerPreferenceHydration } from '@/hooks/useScreenerPreferenceHydration'
import { parseScreenerOrderbookPrefs, type ScreenerOrderbookPrefs } from '@/lib/screenerOrderbookPreferences'
import { ScreenerOrderbookPanel } from '@/components/screener/ScreenerOrderbookPanel'
import { Button } from '@/components/ui/button'

export default function OrderbooksScreener() {
  const { user } = useAuth()
  const account = (user?.username || '').trim().toLowerCase() || 'anonymous'
  const configKey = `screener-orderbook-config:${account}`
  const sourcesKey = `screener-orderbook-sources:${account}`
  const symbolKey = `orderbooks-screener-symbol:${account}`
  const [params, setParams] = useSearchParams()
  const requestedSymbol = (params.get('ticker') || '').trim().toUpperCase()
  const bootstrap = useQuery({ queryKey: ['orderbooks-screener-bootstrap', account],
    queryFn: ({ signal }) => getScreenerTickers(signal), retry: false, staleTime: 60_000 })
  const spotExchanges = useExchangeOptions('orderbook', 'spot')
  const futuresExchanges = useExchangeOptions('orderbook', 'futures')
  const configuredSourceIds = useMemo(() => [
    ...futuresExchanges.map(exchange => `${exchange}:futures`),
    ...spotExchanges.map(exchange => `${exchange}:spot`),
  ], [spotExchanges, futuresExchanges])
  const [symbol, setSymbol] = useState('')
  const [input, setInput] = useState('')
  const [prefs, setPrefs] = useState<ScreenerOrderbookPrefs>(() => parseScreenerOrderbookPrefs(null))
  const hydrate = useCallback(() => {
    let config: string | null = null
    let legacy: string | null = null
    let savedSymbol: string | null = null
    try {
      config = localStorage.getItem(configKey)
      legacy = localStorage.getItem(sourcesKey)
      savedSymbol = localStorage.getItem(symbolKey)
    } catch { /* Browser storage is optional. */ }
    const next = parseScreenerOrderbookPrefs(config, legacy)
    if (config === null && legacy === null) {
      const settings = bootstrap.data?.settings
      const exchanges = settings?.orderbook_filters?.exchanges ?? settings?.exchanges ?? futuresExchanges
      const markets = settings?.orderbook_filters?.markets ?? settings?.markets ?? ['futures', 'spot']
      const candidates = configuredSourceIds.filter(id => {
        const [exchange, market] = id.split(':')
        return exchanges.includes(exchange) && markets.includes(market)
      })
      next.selectedSourceIds = candidates.includes('binance:futures') ? ['binance:futures'] : candidates.slice(0, 1)
    }
    setPrefs(next)
    const nextSymbol = requestedSymbol || savedSymbol || bootstrap.data?.tickers[0]?.symbol || 'BTC'
    setSymbol(nextSymbol)
    setInput(nextSymbol)
  }, [configKey, sourcesKey, symbolKey, requestedSymbol, bootstrap.data, configuredSourceIds, futuresExchanges])
  // Wait for saved filters/history before choosing first-use defaults. A failed
  // bootstrap still permits manually opening a symbol with catalog defaults.
  const ready = bootstrap.isFetched
  const hydrated = useScreenerPreferenceHydration(ready ? configKey : `${configKey}:loading`, useCallback(() => {
    if (ready) hydrate()
  }, [ready, hydrate])) && ready
  useEffect(() => {
    if (!hydrated || !requestedSymbol) return
    setSymbol(requestedSymbol)
    setInput(requestedSymbol)
  }, [requestedSymbol, hydrated])
  useEffect(() => {
    if (!hydrated) return
    try {
      localStorage.setItem(configKey, JSON.stringify(prefs))
      localStorage.setItem(sourcesKey, JSON.stringify(prefs.selectedSourceIds))
      localStorage.setItem(symbolKey, symbol)
    } catch { /* Browser storage is optional. */ }
  }, [hydrated, configKey, sourcesKey, symbolKey, prefs, symbol])
  const presets = useScreenerOrderbookPresets(user, prefs, setPrefs, hydrated)
  const books = useOrderbooksScreenerData(account, symbol, prefs.selectedSourceIds, hydrated)
  const chooseSymbol = () => {
    const next = input.trim().toUpperCase()
    if (!next) return
    setSymbol(next)
    setInput(next)
    setParams(previous => { const nextParams = new URLSearchParams(previous); nextParams.set('ticker', next); return nextParams }, { replace: true })
  }
  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-border/40 px-4 py-3">
        <div className="mr-auto">
          <h1 className="text-lg font-semibold">Orderbooks screener</h1>
          <p className="text-xs text-muted-foreground">Live depth by exchange and market · automatic refresh</p>
        </div>
        <form className="flex items-center gap-2" onSubmit={event => { event.preventDefault(); chooseSymbol() }}>
          <label className="sr-only" htmlFor="orderbooks-symbol">Orderbook ticker</label>
          <input id="orderbooks-symbol" list="orderbooks-history" value={input} onChange={event => setInput(event.target.value)}
            placeholder="BTC or BTCUSDT" maxLength={64} autoComplete="off" spellCheck={false}
            className="w-40 rounded-md border border-border bg-background px-3 py-1.5 text-sm uppercase" />
          <datalist id="orderbooks-history">{bootstrap.data?.tickers.map(ticker => <option key={ticker.symbol} value={ticker.symbol} />)}</datalist>
          <Button type="submit" size="sm" disabled={!hydrated || !input.trim()}>Open</Button>
        </form>
        <Button variant="outline" size="sm" onClick={() => books.refresh()} disabled={!hydrated || books.loading || !prefs.selectedSourceIds.length}>
          <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', books.loading && 'animate-spin')} />Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={() => books.refresh(true)} disabled={!hydrated || books.loading || !prefs.selectedSourceIds.length}>
          Fetch missing books
        </Button>
        <Link className="text-xs text-muted-foreground underline underline-offset-4" to={`/screener?ticker=${encodeURIComponent(symbol)}`}>Market screener</Link>
      </header>
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/30 px-4 py-2 text-xs text-muted-foreground" aria-live="polite">
        <span>Stored depth appears first. Missing books refresh from exchanges automatically; retry with Fetch missing books.</span>
        {books.updatedAt && <span>Updated {books.updatedAt.toLocaleTimeString('en-GB', { timeZone: 'UTC', hour12: false })} UTC</span>}
        {books.loading && <span>Updating…</span>}
      </div>
      {(books.error || bootstrap.isError) && <div role="alert" className="flex shrink-0 items-center gap-2 bg-amber-500/10 px-4 py-2 text-xs text-amber-500">
        <AlertCircle className="h-4 w-4" />{books.error || 'Saved ticker history is unavailable. You can still open a symbol.'}
      </div>}
      {hydrated ? <ScreenerOrderbookPanel standalone presets={presets} symbol={symbol} tickerData={books.data}
        selectedSourceIds={prefs.selectedSourceIds}
        onSelectedSourceIdsChange={selectedSourceIds => setPrefs(previous => ({ ...previous, selectedSourceIds,
          collapsedSourceIds: previous.collapsedSourceIds.filter(id => selectedSourceIds.includes(id)) }))}
        collapsedSourceIds={prefs.collapsedSourceIds}
        onCollapsedSourceIdsChange={collapsedSourceIds => setPrefs(previous => ({ ...previous, collapsedSourceIds }))}
        compression={prefs.compression} onCompressionChange={compression => setPrefs(previous => ({ ...previous, compression }))}
        compressionOverrides={prefs.compressionOverrides}
        onCompressionOverridesChange={compressionOverrides => setPrefs(previous => ({ ...previous, compressionOverrides }))}
        configuredSourceIds={configuredSourceIds} availableExchanges={books.data?.available_exchanges || []} />
        : <div className="p-6 text-sm text-muted-foreground">Loading orderbook preferences…</div>}
    </div>
  )
}
